import {
  ALGORITHM_LABEL,
  KDF_LABEL,
  PRODUCTION_ITERATIONS,
  base64ToBytes,
  deriveKey,
  type CryptoOptions,
  type EncryptedEnvelope,
} from './encrypt'

export class WrongPasswordError extends Error {
  constructor() {
    super('senha incorreta')
    this.name = 'WrongPasswordError'
  }
}

export class CorruptedFileError extends Error {
  constructor(reason: string) {
    super(`arquivo corrompido: ${reason}`)
    this.name = 'CorruptedFileError'
  }
}

export class UnsupportedEnvelopeError extends Error {
  constructor(reason: string) {
    super(`envelope não suportado: ${reason}`)
    this.name = 'UnsupportedEnvelopeError'
  }
}

export async function decryptString(
  envelope: EncryptedEnvelope,
  password: string,
  options: CryptoOptions = {},
): Promise<string> {
  if (envelope.algorithm !== ALGORITHM_LABEL) {
    throw new UnsupportedEnvelopeError(`algorithm=${envelope.algorithm}`)
  }
  if (envelope.kdf !== KDF_LABEL) {
    throw new UnsupportedEnvelopeError(`kdf=${envelope.kdf}`)
  }
  const iterations = options.iterations ?? PRODUCTION_ITERATIONS

  let salt: Uint8Array
  let iv: Uint8Array
  let ciphertext: Uint8Array
  try {
    salt = base64ToBytes(envelope.salt)
    iv = base64ToBytes(envelope.iv)
    ciphertext = base64ToBytes(envelope.ciphertext)
  } catch (err) {
    throw new CorruptedFileError(err instanceof Error ? err.message : 'base64 inválido')
  }

  let key: CryptoKey
  try {
    key = await deriveKey(password, salt, iterations)
  } catch (err) {
    throw new CorruptedFileError(
      `falha ao derivar chave: ${err instanceof Error ? err.message : String(err)}`,
    )
  }

  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      ciphertext as BufferSource,
    )
    return new TextDecoder().decode(plaintext)
  } catch {
    // AES-GCM authentication failure: either wrong password or tampered
    // ciphertext. We surface this as WrongPasswordError because the UI
    // gives the user a chance to retype the password.
    throw new WrongPasswordError()
  }
}
